// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
} from 'common.data';
import type {
  $DateTimeFormats,
} from 'common.helpers';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';
import {
  RegattaStatus,
} from 'common.data';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getDateTimeFormats,
} from 'common.helpers/dist/browser';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $Model<'Regatta'>;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $Day = {
  Name: string;
  RegattaSchedules: Array<$Model<'RegattaSchedule'>>;
  timeFormat: string;
};

type $State = {
  data: Array<$Model<'RegattaSchedule'>>;
  loading: boolean;
  parsedData: Array<$Day>;
};

class SchedulesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      parsedData: [],
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
      main,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaSchedulesSectionData({
        RegattaId: item.id,
      });

      const formats: $DateTimeFormats = getDateTimeFormats({
        RegionId: main.RegionId,
        regions: main.regions,
      });

      const parsedData: Array<$Day> = _.map(
        _.groupBy(
          data,
          (dataItem: $Model<'RegattaSchedule'>) => moment(dataItem.StartDate).format(formats.Date),
        ),
        (RegattaSchedules: Array<$Model<'RegattaSchedule'>>, Name: string) => ({
          Name,
          RegattaSchedules,
          timeFormat: formats.Time,
        }),
      );

      await this.setStateAsync({
        data,
        loading: false,
        parsedData,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaSchedule') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  getRegattaDayClasses(day: $Day): string {
    const {
      item,
    } = this.props;

    const now: moment.Moment = moment();

    const regattaDayDateMoment = moment(
      day.Name,
      'YYYY-MM-DD',
    ).endOf('day');

    const ended: boolean = now.isAfter(regattaDayDateMoment);

    let output = '';

    if (ended === true && item.Status !== RegattaStatus.ended) {
      output = `${output} opacity-50 hidden sm:block`;
    }

    return output;
  }

  renderEvent(item: $Model<'RegattaSchedule'>, timeFormat: string) {
    const {
      t,
    } = this.props;

    const startTime: string = moment(item.StartDate).format(timeFormat);

    const times: Array<string> = [startTime];

    if (item.EndDate) {
      const endTime: string = moment(item.EndDate).format(timeFormat);

      times.push(endTime);
    }

    return (
      <div
        className={'flex flex-row mb-2'}
        key={item.id}
        title={t('Event')}
      >
        <div className={'basis-1/3 leading-tight pr-2'}>
          {times.join(' - ')}
        </div>
        <div className={'basis-2/3 leading-tight font-medium'}>
          <div className={'uppercase'}>
            {item.Name}
          </div>
          <small className={'text-gray-600'}>
            {item.Content}
          </small>
        </div>
      </div>
    );
  }

  renderDay(day: $Day) {
    const {
      item,
      t,
    } = this.props;

    const regattaStartDateMoment = moment(item.StartDate);
    const regattaDayDateMoment = moment(
      day.Name,
      'YYYY-MM-DD',
    );
    const dateDiff = regattaDayDateMoment.diff(regattaStartDateMoment);
    const dayIndex: number = moment.duration(dateDiff).asDays();
    const dayNo: number = Math.ceil(dayIndex + 1);
    const weekDay: string = regattaDayDateMoment.format('ddd');

    return (
      <div
        className={`rounded-lg bg-white overflow-hidden relative max-w-sm flex items-start p-5 ${this.getRegattaDayClasses(day)}`}
        key={day.Name}
      >
        <div className={'flex flex-col w-full'}>
          <div className={'h4 uppercase flex flex-row'}>
            <div className={'basis-1/3 pr-2'}>
              <span className={'text-blue-900'}>
                {`${t('Day')}: `}
              </span>
              <span className={'text-gray-600'}>
                {dayNo}
              </span>
            </div>
            <div className={'basis-2/3'}>
              <span className={'text-blue-900'}>
                {weekDay}
              </span>
              <span className={'text-gray-600'}>
                {` | ${day.Name}`}
              </span>
            </div>
          </div>
          <hr className={'mb-2'} />
          {day.RegattaSchedules.map((event: $Model<'RegattaSchedule'>) => this.renderEvent(
            event,
            day.timeFormat,
          ))}
        </div>
      </div>
    );
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
      parsedData,
    } = this.state;

    const shorten: boolean = data.length > 7;

    return (data.length > 0 && parsedData.length > 0 ? (
      <Section
        id={webSections[webPages.regattaPage].schedule}
        shorten={shorten}
        shortenButtonLabel={t('Show full schedule')}
        title={t('Schedule')}
        transparent
      >
        <Loader
          fade
          loading={loading === true}
        >
          <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 ${shorten === true ? '' : '-mx-5 -mb-5'}`}>
            {parsedData.map((parsedItem: $Day) => this.renderDay(parsedItem))}
          </div>
        </Loader>
      </Section>
    ) : null);
  }
}

export default SchedulesSectionView;
